// src/api/passkey.ts — WebAuthn (FIDO2) 通行密钥 客户端 API.
//
// 浏览器调用流程:
//   1. begin-registration → 拿到 challenge + rp 等
//   2. PublicKeyCredentialCreationOptions.from(JSON) → navigator.credentials.create()
//   3. 把结果 (attestationObject / clientDataJSON / credential.id) base64url
//      后通过 finish-registration 发回
//   登录流程类似: begin-login → navigator.credentials.get() → finish-login.
//
// 卡巴斯基 / Windows Hello / Google Password Manager 都会暴露同一个
// navigator.credentials.* API, 不需要适配.

import api from './client'

// --- shared types (camelCase from server JSON) ---

export interface PublicKeyCreationOptionsJSON {
  rp: { id: string; name: string }
  user: { id: string; name: string; displayName: string }
  challenge: string
  pubKeyCredParams: Array<{ type: 'public-key'; alg: number }>
  timeout: number
  attestation: 'none' | 'direct' | 'indirect'
  authenticatorSelection?: {
    userVerification: 'preferred' | 'discouraged' | 'required'
    residentKey?: 'preferred' | 'discouraged' | 'required'
  }
  excludeCredentials?: Array<{ type: 'public-key'; id: string }>
}

export interface PublicKeyRequestOptionsJSON {
  challenge: string
  timeout: number
  rpId: string
  allowCredentials: Array<{ type: 'public-key'; id: string }>
  userVerification: 'preferred' | 'discouraged' | 'required'
}

export interface PasskeyInfo {
  id: string // base64url credential_id
  label: string
  created_at: string
  last_used_at: string
  sign_count: number
}

// --- server calls ---

export async function passkeyBeginRegistration(label = 'Admin passkey'): Promise<PublicKeyCreationOptionsJSON> {
  return api.post<PublicKeyCreationOptionsJSON>(
    '/api/web-auth/passkey/begin-registration',
    { label },
    { skipAuthRedirect: true },
  )
}

export async function passkeyFinishRegistration(payload: {
  id: string
  attestationObject: string
  clientDataJSON: string
  label?: string
}): Promise<{ ok: boolean; id: string }> {
  return api.post<{ ok: boolean; id: string }>(
    '/api/web-auth/passkey/finish-registration',
    payload,
    { skipAuthRedirect: true },
  )
}

export async function passkeyList(): Promise<{ keys: PasskeyInfo[] }> {
  return api.get<{ keys: PasskeyInfo[] }>('/api/web-auth/passkey/list')
}

export async function passkeyDelete(id: string): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>('/api/web-auth/passkey/delete', { id })
}

export async function passkeyBeginLogin(username?: string): Promise<PublicKeyRequestOptionsJSON> {
  return api.post<PublicKeyRequestOptionsJSON>(
    '/api/web-auth/passkey/begin-login',
    username ? { username } : {},
    { skipAuthRedirect: true },
  )
}

export interface PasskeyLoginResponse {
  ok: boolean
  username: string
  user: { username: string; is_default?: boolean }
  token: { value: string; authenticated: boolean }
}

export async function passkeyFinishLogin(payload: {
  id: string
  authenticatorData: string
  clientDataJSON: string
  signature: string
}): Promise<PasskeyLoginResponse> {
  return api.post<PasskeyLoginResponse>(
    '/api/web-auth/passkey/finish-login',
    payload,
    { skipAuthRedirect: true },
  )
}

// --- helpers: 标准 WebAuthn 调用 + base64url 转码 ---

export function b64uToBytes(s: string): Uint8Array {
  // base64url → base64 → atob → bytes
  const padded = s.replace(/-/g, '+').replace(/_/g, '/').padEnd(s.length + (4 - (s.length % 4)) % 4, '=')
  const bin = atob(padded)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

export function bytesToB64u(b: ArrayBuffer | Uint8Array): string {
  const bytes = b instanceof Uint8Array ? b : new Uint8Array(b)
  let s = ''
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i])
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export interface PasskeySupport {
  available: boolean
  reason?: string
}

/** 探测环境是否支持 passkey (HTTPS / secure context / 浏览器 API 都在). */
export async function probePasskeySupport(): Promise<PasskeySupport> {
  if (typeof window === 'undefined') return { available: false, reason: 'no window' }
  if (!('PublicKeyCredential' in window)) {
    return { available: false, reason: 'PublicKeyCredential API not available' }
  }
  return { available: true }
}

/** 服务端是否已注册过通行密钥 (公开端点, 登录页未登录时调用). 没注册就隐藏 passkey 入口. */
export async function passkeyServerAvailable(): Promise<boolean> {
  try {
    const resp = await api.get<{ available: boolean }>('/api/web-auth/passkey/available', {
      skipAuthRedirect: true,
    })
    return !!resp.available
  } catch {
    return false
  }
}

/** 用户名 → user.id (稳定哈希, 后端验签时不依赖前端实现细节). */
export function userIdFor(username: string): Uint8Array {
  const enc = new TextEncoder().encode(username)
  return crypto.subtle ? new Uint8Array(0) : enc // 由调用方走 hash
}

// 包装 navigator.credentials.create — 把 server 的 JSON 转成 CreateOptions,
// 把返回的 PublicKeyCredential 转成 base64url 字段.
export async function createPasskey(opts: PublicKeyCreationOptionsJSON): Promise<{
  id: string
  attestationObject: string
  clientDataJSON: string
}> {
  // challenge / user.id / excludeCredentials[].id 都从 base64url 转 ArrayBuffer
  // v3.13.39: 强制 residentKey=required, 让 usernameless login 能用.
  const authSel = opts.authenticatorSelection ?? {
    userVerification: 'preferred',
    residentKey: 'required',
  }
  const createOptions: CredentialCreationOptions = {
    publicKey: {
      ...opts,
      challenge: b64uToBytes(opts.challenge).buffer,
      user: { ...opts.user, id: b64uToBytes(opts.user.id).buffer },
      excludeCredentials: opts.excludeCredentials?.map(c => ({
        type: c.type,
        id: b64uToBytes(c.id).buffer,
      })),
      pubKeyCredParams: opts.pubKeyCredParams.map(p => ({
        type: p.type,
        alg: p.alg,
      })),
      rp: { id: opts.rp.id, name: opts.rp.name },
      // timeout 是毫秒, PublicKeyCredentialParameters 同样
      timeout: opts.timeout,
      attestation: opts.attestation,
      authenticatorSelection: { ...authSel, residentKey: 'required' },
    },
  }
  // 实际调 navigator.credentials.create (在非安全上下文会抛 SecurityError).
  const cred = (await navigator.credentials.create(createOptions)) as PublicKeyCredential
  if (!cred) {
    throw new Error('user cancelled passkey registration')
  }
  const resp = cred.response as AuthenticatorAttestationResponse
  return {
    id: bytesToB64u(cred.rawId),
    attestationObject: bytesToB64u(resp.attestationObject),
    clientDataJSON: bytesToB64u(resp.clientDataJSON),
  }
}

export async function getPasskey(opts: PublicKeyRequestOptionsJSON): Promise<{
  id: string
  authenticatorData: string
  clientDataJSON: string
  signature: string
}> {
  // v3.13.39: usernameless 流程 — allowCredentials 为空时直接传空数组,
  // 浏览器会弹所有该 rp.id 下已注册的 passkey.
  const allowCredentials = (opts.allowCredentials ?? []).map(c => ({
    type: c.type,
    id: b64uToBytes(c.id).buffer,
  }))
  const getOptions: CredentialRequestOptions = {
    publicKey: {
      ...opts,
      challenge: b64uToBytes(opts.challenge).buffer,
      allowCredentials,
      rpId: opts.rpId,
      timeout: opts.timeout,
      userVerification: opts.userVerification,
    },
  }
  const cred = (await navigator.credentials.get(getOptions)) as PublicKeyCredential
  if (!cred) {
    throw new Error('user cancelled passkey login')
  }
  const resp = cred.response as AuthenticatorAssertionResponse
  return {
    id: bytesToB64u(cred.rawId),
    authenticatorData: bytesToB64u(resp.authenticatorData),
    clientDataJSON: bytesToB64u(resp.clientDataJSON),
    signature: bytesToB64u(resp.signature),
  }
}
